import { useCartDispatch, useCartState } from "hooks/useCartV2"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { ImprovedCartActionsEnum } from "types/improved-booking"

function stripLocalePrefix(path: string) {
    return path.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/)/, "")
}

function isBookingFlowPath(url: string) {
    const [pathWithoutHash] = url.split("#")
    const [pathOnly] = pathWithoutHash.split("?")
    const normalizedPath = stripLocalePrefix(pathOnly)

    return /^\/events\/[^/]+\/book(?:\/checkout)?\/?$/.test(normalizedPath)
}

function getEventIdFromPath(path: string) {
    const normalizedPath = stripLocalePrefix(path)
    const matches = normalizedPath.match(/^\/events\/([^/]+)/)
    return matches ? matches[1] : null
}

export function useClearBookingCartOnLeave() {
    const router = useRouter()
    const cartDispatch = useCartDispatch()
    const cartState = useCartState()

    useEffect(() => {
        const handleRouteChangeComplete = (url: string) => {
            const [pathWithoutHash] = url.split("#")
            const [pathOnly] = pathWithoutHash.split("?")

            if (!isBookingFlowPath(url)) {
                cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
                return
            }

            const nextEventIdOrSlug = getEventIdFromPath(pathOnly)
            const knownEventIds = [
                cartState.event,
                cartState.event_id,
                cartState.eventSlug,
            ].filter(
                (value): value is string =>
                    typeof value === "string" && value.length > 0
            )

            if (
                nextEventIdOrSlug &&
                knownEventIds.length > 0 &&
                !knownEventIds.includes(nextEventIdOrSlug)
            ) {
                cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
            }
        }

        router.events.on("routeChangeComplete", handleRouteChangeComplete)

        return () => {
            router.events.off("routeChangeComplete", handleRouteChangeComplete)
        }
    }, [
        cartDispatch,
        router.events,
        cartState.event,
        cartState.event_id,
        cartState.eventSlug,
    ])
}
